import React, { useState, useEffect } from 'react';

const DurableDashboard = ({ instruments, supplies, itemMaster, logs = [] }) => {
  const [selectedStage, setSelectedStage] = useState('sterile');
  const [selectedItemDetail, setSelectedItemDetail] = useState(null);
  const [selectedClinicItemDetail, setSelectedClinicItemDetail] = useState(null);
  const [selectedLocationDetail, setSelectedLocationDetail] = useState(null);
  const [selectedWashingDetail, setSelectedWashingDetail] = useState(null);
  const [filterDate, setFilterDate] = useState(() => {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  });

  useEffect(() => {
    setSelectedItemDetail(null);
    setSelectedClinicItemDetail(null);
    setSelectedLocationDetail(null);
    setSelectedWashingDetail(null);
  }, [selectedStage]);

  const stageNames = {
    sterile: 'ห้อง Sterile',
    clinic: 'ห้องคลีนิก',
    washing: 'ห้องล้างเครื่องมือ',
    sterilizing: 'ห้องนึ่งฆ่าเชื้อ'
  };

  // กรองสินค้าที่เป็นวัสดุคงทน
  const durableMasters = itemMaster.filter(m => m.category === 'วัสดุคงทน');
  const durableMasterIds = durableMasters.map(m => m.id);

  // 1. ห้อง Sterile
  const sterileItems = instruments.filter(i =>
    i.is_active &&
    i.status === 'sterile-ready' &&
    durableMasterIds.includes(i.master_id)
  );
  const sterileItemsFiltered = filterDate
    ? sterileItems.filter(item => item.details?.mfgDate === filterDate || item.last_updated?.startsWith(filterDate))
    : sterileItems;

  // 2. ห้องคลีนิก (รวมห้องคลีนิก 1-7, PCU/ออกหน่วย, OR, เรือนจำ)
  const clinicItems = instruments.filter(i =>
    i.is_active &&
    i.status === 'in-use' &&
    durableMasterIds.includes(i.master_id)
  );
  const clinicItemsFiltered = filterDate
    ? clinicItems.filter(item => item.last_updated?.startsWith(filterDate))
    : clinicItems;

  // 3. ห้องล้างเครื่องมือ (ข้อมูลมาจาก logs และ instruments)
  const washingLogs = (logs || []).filter(log =>
    log.warehouse === 'ห้องล้างเครื่องมือ' &&
    (log.action.includes('รับคืน รหัส') || log.action.includes('รับคืน (แยกชุด) รหัส'))
  );

  // ดึงข้อมูลการรับคืนในห้องล้างเครื่องมือ และจับกลุ่มตาม วันที่ + ชื่อวัสดุคงทน
  const groupedWashingItems = (() => {
    const groups = {};

    durableMasters.forEach(master => {
      const sup = supplies.find(s => s.master_id === master.id && s.warehouse_name.includes("ห้องล้างเครื่องมือ"));
      const currentCount = sup ? (sup.item_amount || 0) : 0;
      if (currentCount <= 0) return;

      const masterLogs = washingLogs
        .filter(log => log.item_name === master.item_name)
        .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

      const activeLogs = masterLogs.slice(0, currentCount);

      // ใส่ข้อมูลจาก log
      activeLogs.forEach(log => {
        const barcodeMatch = log.action.match(/รหัส\s+([A-Za-z0-9-]+)/);
        const barcode = barcodeMatch ? barcodeMatch[1] : null;
        if (!barcode) return;

        const scannerMatch = log.action.match(/โดย\s+(\S+)/);
        const scanner = scannerMatch ? scannerMatch[1] : 'ไม่ระบุ';

        const dateObj = new Date(log.timestamp);
        const formattedDate = dateObj.toLocaleDateString('th-TH', { year: 'numeric', month: 'short', day: 'numeric' });

        const inst = instruments.find(i => i.unique_barcode === barcode);
        const itemDetail = {
          barcode,
          scanner,
          timestamp: log.timestamp,
          mfgDate: inst?.details?.mfgDate || '-',
          expDate: inst?.details?.expDate || '-',
          preparedBy: inst?.details?.preparedBy || '-',
          checkedBy: inst?.details?.checkedBy || '-',
          machine: inst?.details?.machine || '-',
          cycle: inst?.details?.cycle || '-',
          location: inst?.location || 'ไม่ระบุ',
        };

        const groupKey = `${formattedDate}_${log.item_name}`;
        if (!groups[groupKey]) {
          groups[groupKey] = {
            date: formattedDate,
            rawDate: dateObj,
            item_name: log.item_name,
            master,
            scanners: new Set(),
            items: [],
          };
        }
        groups[groupKey].scanners.add(scanner);
        groups[groupKey].items.push(itemDetail);
      });

      // ถ้าจำนวน logs น้อยกว่าจำนวนของที่ค้างใน supplies ให้เพิ่ม placeholder
      const missingLogsCount = currentCount - activeLogs.length;
      for (let i = 0; i < missingLogsCount; i++) {
        const formattedDate = 'ไม่ระบุวันที่';
        const itemDetail = {
          barcode: '-',
          scanner: 'ไม่ระบุ',
          timestamp: new Date(0).toISOString(),
          mfgDate: '-',
          expDate: '-',
          preparedBy: '-',
          checkedBy: '-',
          machine: '-',
          cycle: '-',
          location: 'ไม่ระบุ',
        };

        const groupKey = `${formattedDate}_${master.item_name}`;
        if (!groups[groupKey]) {
          groups[groupKey] = {
            date: formattedDate,
            rawDate: new Date(0),
            item_name: master.item_name,
            master,
            scanners: new Set(),
            items: [],
          };
        }
        groups[groupKey].scanners.add('ไม่ระบุ');
        groups[groupKey].items.push(itemDetail);
      }
    });

    return Object.values(groups)
      .map(group => ({
        ...group,
        scanners: Array.from(group.scanners),
      }))
      .sort((a, b) => b.rawDate - a.rawDate);
  })();

  const washingCount = groupedWashingItems.reduce((sum, g) => sum + g.items.length, 0);

  const groupedWashingItemsFiltered = filterDate
    ? groupedWashingItems.filter(group => {
        if (!group.rawDate || group.date === 'ไม่ระบุวันที่') return false;
        const yyyy = group.rawDate.getFullYear();
        const mm = String(group.rawDate.getMonth() + 1).padStart(2, '0');
        const dd = String(group.rawDate.getDate()).padStart(2, '0');
        const groupDateStr = `${yyyy}-${mm}-${dd}`;
        return groupDateStr === filterDate;
      })
    : groupedWashingItems;

  const washingCountFiltered = groupedWashingItemsFiltered.reduce((sum, g) => sum + g.items.length, 0);

  // 4. ห้องนึ่งฆ่าเชื้อ (กำลัง sterile)
  const sterilizingItems = instruments.filter(i =>
    i.is_active &&
    i.status === 'sterile-pending' &&
    durableMasterIds.includes(i.master_id)
  );
  const sterilizingItemsFiltered = filterDate
    ? sterilizingItems.filter(item => item.last_updated?.startsWith(filterDate))
    : sterilizingItems;

  // ข้อมูลแต่ละขั้นตอนการนึ่ง
  const stages = {
    sterile: {
      id: 'sterile',
      title: 'ห้อง Sterile',
      subtitle: 'Sterile Storage',
      count: sterileItems.length,
      icon: '🏥',
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      shadowColor: 'rgba(102, 126, 234, 0.4)',
    },
    clinic: {
      id: 'clinic',
      title: 'ห้องคลีนิก',
      subtitle: 'Clinic Usage',
      count: clinicItems.length,
      icon: '🦷',
      gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      shadowColor: 'rgba(245, 87, 108, 0.4)',
    },
    washing: {
      id: 'washing',
      title: 'ห้องล้างเครื่องมือ',
      subtitle: 'Decontamination',
      count: washingCount,
      icon: '🧽',
      gradient: 'linear-gradient(135deg, #f6d365 0%, #fda085 100%)',
      shadowColor: 'rgba(253, 160, 133, 0.4)',
    },
    sterilizing: {
      id: 'sterilizing',
      title: 'ห้องนึ่งฆ่าเชื้อ',
      subtitle: 'Sterilizing (กำลังนึ่ง)',
      count: sterilizingItems.length,
      icon: '♨️',
      gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      shadowColor: 'rgba(79, 172, 254, 0.4)',
    }
  };

  const getStageTitle = (id) => {
    return stages[id]?.title || '';
  };

  const getDaysRemaining = (expDateStr) => {
    if (!expDateStr) return null;
    const expDate = new Date(expDateStr);
    const today = new Date();
    expDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    const diffTime = expDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getItemLogStats = (itemName) => {
    const filteredLogs = filterDate
      ? (logs || []).filter(log => log.timestamp?.startsWith(filterDate))
      : (logs || []);

    const sterileIn = filteredLogs.filter(log =>
      log.item_name === itemName &&
      log.warehouse === 'ห้อง sterile' &&
      (log.action.includes('รับเข้านึ่งเสร็จ') || log.action.includes('รับคืน (ไม่ได้ใช้)'))
    ).length;

    const sterileOut = filteredLogs.filter(log =>
      log.item_name === itemName &&
      log.action.includes('จ่ายออก รหัส')
    ).length;

    const clinicIn = sterileOut;

    const clinicOut = filteredLogs.filter(log =>
      log.item_name === itemName &&
      (
        (log.warehouse === 'ห้องล้างเครื่องมือ' && (log.action.includes('รับคืน รหัส') || log.action.includes('รับคืน (แยกชุด) รหัส'))) ||
        (log.warehouse === 'ห้อง sterile' && log.action.includes('รับคืน (ไม่ได้ใช้)'))
      )
    ).length;

    const washingIn = filteredLogs.filter(log =>
      log.item_name === itemName &&
      log.warehouse === 'ห้องล้างเครื่องมือ' &&
      (log.action.includes('รับคืน รหัส') || log.action.includes('รับคืน (แยกชุด) รหัส'))
    ).length;

    const washingOut = filteredLogs.filter(log =>
      log.item_name === itemName &&
      log.warehouse === 'ห้องล้างเครื่องมือ' &&
      (log.action.includes('แพ็กซองสร้างรหัสใหม่') || log.action.includes('ผูกข้อมูลกับฉลากเปล่า') || log.action.includes('ผูกข้อมูลฉลากเปล่า'))
    ).length;

    const sterilizingIn = washingOut;

    const sterilizingOut = filteredLogs.filter(log =>
      log.item_name === itemName &&
      log.warehouse === 'ห้อง sterile' &&
      log.action.includes('รับเข้านึ่งเสร็จ')
    ).length;

    return {
      sterile: { in: sterileIn, out: sterileOut },
      clinic: { in: clinicIn, out: clinicOut },
      washing: { in: washingIn, out: washingOut },
      sterilizing: { in: sterilizingIn, out: sterilizingOut }
    };
  };

  const getStageTotalStats = () => {
    let totalIn = 0;
    let totalOut = 0;

    durableMasters.forEach(master => {
      const stats = getItemLogStats(master.item_name);
      if (selectedStage === 'sterile') {
        totalIn += stats.sterile.in;
        totalOut += stats.sterile.out;
      } else if (selectedStage === 'clinic') {
        totalIn += stats.clinic.in;
        totalOut += stats.clinic.out;
      } else if (selectedStage === 'washing') {
        totalIn += stats.washing.in;
        totalOut += stats.washing.out;
      } else if (selectedStage === 'sterilizing') {
        totalIn += stats.sterilizing.in;
        totalOut += stats.sterilizing.out;
      }
    });

    let currentTotal = 0;
    if (selectedStage === 'sterile') {
      currentTotal = sterileItemsFiltered.length;
    } else if (selectedStage === 'clinic') {
      currentTotal = clinicItemsFiltered.length;
    } else if (selectedStage === 'washing') {
      currentTotal = washingCountFiltered;
    } else if (selectedStage === 'sterilizing') {
      currentTotal = sterilizingItemsFiltered.length;
    }

    return { in: totalIn, total: currentTotal, out: totalOut };
  };

  const stageTotalStats = getStageTotalStats();

  const getTopUsedItems = () => {
    const usageCounts = {};

    durableMasters.forEach(master => {
      usageCounts[master.item_name] = 0;
    });

    const filteredLogs = filterDate
      ? (logs || []).filter(log => log.timestamp?.startsWith(filterDate))
      : (logs || []);

    filteredLogs.forEach(log => {
      if (log.action.includes('จ่ายออก') && usageCounts[log.item_name] !== undefined) {
        usageCounts[log.item_name] += 1;
      }
    });

    return Object.entries(usageCounts)
      .map(([name, count]) => {
        const master = durableMasters.find(m => m.item_name === name);
        return { name, count, master };
      })
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);
  };

  const topUsedItems = getTopUsedItems();

  // SVG Path Generator สำหรับสร้างวงจรลูกศรขี้ล็อกเกอร์ตามเข็มนาฬิกา
  const getArrowSegmentPath = (startDeg, endDeg) => {
    const cx = 300;
    const cy = 300;
    const R = 245; // รัศมีวงนอก
    const r = 125; // รัศมีวงใน
    const m = (R + r) / 2; // รัศมีกึ่งกลางสำหรับจุดยอดแหลมของหัวลูกศร

    const degToRad = (deg) => (deg * Math.PI) / 180;

    // delta คือความลึก/ยื่นของหัวลูกศร (หน่วยเป็นองศา)
    const delta = 10;

    const A1 = degToRad(startDeg);
    const A2 = degToRad(endDeg);
    const A1_indent = degToRad(startDeg + delta);
    const A2_point = degToRad(endDeg + delta);

    // พิกัดจุดเริ่มที่ถูกลึกเข้าไป (เว้าตามหัวลูกศรก่อนหน้า)
    const x_inner_start = cx + r * Math.cos(A1);
    const y_inner_start = cy + r * Math.sin(A1);
    const x_mid_indent = cx + m * Math.cos(A1_indent);
    const y_mid_indent = cy + m * Math.sin(A1_indent);
    const x_outer_start = cx + R * Math.cos(A1);
    const y_outer_start = cy + R * Math.sin(A1);

    // พิกัดจุดปลายที่ยื่นออกเป็นหัวลูกศรแหลมชี้ไปข้างหน้า
    const x_outer_end = cx + R * Math.cos(A2);
    const y_outer_end = cy + R * Math.sin(A2);
    const x_mid_point = cx + m * Math.cos(A2_point);
    const y_mid_point = cy + m * Math.sin(A2_point);
    const x_inner_end = cx + r * Math.cos(A2);
    const y_inner_end = cy + r * Math.sin(A2);

    return `
      M ${x_inner_start} ${y_inner_start}
      L ${x_mid_indent} ${y_mid_indent}
      L ${x_outer_start} ${y_outer_start}
      A ${R} ${R} 0 0 1 ${x_outer_end} ${y_outer_end}
      L ${x_mid_point} ${y_mid_point}
      L ${x_inner_end} ${y_inner_end}
      A ${r} ${r} 0 0 0 ${x_inner_start} ${y_inner_start}
      Z
    `;
  };

  return (
    <div style={{
      background: '#111827', // Opaque dark background
      borderRadius: '20px',
      border: '1px solid rgba(255,255,255,0.1)',
      padding: '30px',
      margin: '20px auto',
      color: '#ffffff',
      fontFamily: "'Segoe UI', 'Noto Sans Thai', -apple-system, sans-serif"
    }}>
      {/* Title */}
      <div style={{ textAlign: 'center', marginBottom: '35px', animation: 'fadeInDown 0.6s ease-out both' }}>
        <h2 style={{ fontSize: '1.8em', fontWeight: 'bold', margin: '0 0 10px 0', color: '#1abc9c' }}>
          📊 แดชบอร์ดวงจรวัสดุคงทน (Durable Lifecycle Dashboard)
        </h2>
        <p style={{ color: 'rgba(255,255,255,0.7)', margin: 0, fontSize: '0.95em' }}>
          ตรวจสอบสรุปสถิติจำนวนและข้อมูลอุปกรณ์คงทนแบบบาร์โค้ดตาม 4 ขั้นตอนวัฏจักรงาน CSSD
        </p>
      </div>

      {/* ==================== DESKTOP WORKFLOW (CIRCULAR INTERLOCKING ARROWS) ==================== */}
      <div className="desktop-lifecycle" style={{
        position: 'relative',
        width: '600px',
        height: '600px',
        margin: '0 auto 40px auto',
        display: 'block',
        animation: 'fadeInScale 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94) both'
      }}>
        {/* SVG Drawing the sectors */}
        <svg style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 1 }} viewBox="0 0 600 600">
          {/* Stage 1: Sterile (Top-Left: 180 to 270 deg) */}
          <path
            d={getArrowSegmentPath(180, 270)}
            fill={selectedStage === 'sterile' ? 'rgba(99, 102, 241, 0.25)' : 'rgba(255, 255, 255, 0.05)'}
            stroke="#111827"
            strokeWidth="8"
            style={{ cursor: 'pointer', transition: 'all 0.3s' }}
            onClick={() => setSelectedStage('sterile')}
          />

          {/* Stage 2: Clinic (Top-Right: 270 to 360 deg) */}
          <path
            d={getArrowSegmentPath(270, 360)}
            fill={selectedStage === 'clinic' ? 'rgba(244, 63, 94, 0.25)' : 'rgba(255, 255, 255, 0.05)'}
            stroke="#111827"
            strokeWidth="8"
            style={{ cursor: 'pointer', transition: 'all 0.3s' }}
            onClick={() => setSelectedStage('clinic')}
          />

          {/* Stage 3: Washing (Bottom-Right: 0 to 90 deg) */}
          <path
            d={getArrowSegmentPath(0, 90)}
            fill={selectedStage === 'washing' ? 'rgba(245, 158, 11, 0.25)' : 'rgba(255, 255, 255, 0.05)'}
            stroke="#111827"
            strokeWidth="8"
            style={{ cursor: 'pointer', transition: 'all 0.3s' }}
            onClick={() => setSelectedStage('washing')}
          />

          {/* Stage 4: Sterilizing (Bottom-Left: 90 to 180 deg) */}
          <path
            d={getArrowSegmentPath(90, 180)}
            fill={selectedStage === 'sterilizing' ? 'rgba(6, 182, 212, 0.25)' : 'rgba(255, 255, 255, 0.05)'}
            stroke="#111827"
            strokeWidth="8"
            style={{ cursor: 'pointer', transition: 'all 0.3s' }}
            onClick={() => setSelectedStage('sterilizing')}
          />
        </svg>

        {/* Center Cycle Cutout */}
        <div style={{
          position: 'absolute',
          top: '300px',
          left: '300px',
          transform: 'translate(-50%, -50%)',
          width: '240px',
          height: '240px',
          borderRadius: '50%',
          background: '#111827', // กลมกลืนไปกับพื้นหลังทึบ
          border: '1px solid rgba(255,255,255,0.08)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 4,
          pointerEvents: 'none',
          boxShadow: 'inset 0 0 20px rgba(0,0,0,0.6)',
          animation: 'fadeInCentroid 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.1s both'
        }}>
          {/* Room Icon (growing on stage change) */}
          <span
            key={`${selectedStage}-icon`}
            style={{
              fontSize: '3.8em',
              marginBottom: '10px',
              textShadow: `0 0 15px ${stages[selectedStage]?.shadowColor || 'rgba(26,188,156,0.3)'}`,
              animation: 'fadeInScale 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94) both',
              display: 'inline-block'
            }}
          >
            {stages[selectedStage]?.icon}
          </span>
          {/* Room Name */}
          <span
            key={`${selectedStage}-name`}
            style={{
              fontSize: '1.25em',
              fontWeight: 'bold',
              color: '#1abc9c',
              textAlign: 'center',
              padding: '0 15px',
              wordBreak: 'break-word',
              textShadow: '0 0 10px rgba(26,188,156,0.3)',
              animation: 'fadeInDown 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94) both'
            }}
          >
            {stageNames[selectedStage]}
          </span>
        </div>

        {/* Interactive Centroid Labels overlay */}
        {/* Stage 1: Sterile */}
        <div
          onClick={() => setSelectedStage('sterile')}
          style={getCentroidLabelStyle(175, 175, selectedStage === 'sterile', 0.2)}
        >
          <span style={{
            fontSize: '2.4em',
            transform: selectedStage === 'sterile' ? 'scale(1.3)' : 'scale(1)',
            transition: 'transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
            display: 'inline-block'
          }}>🏥</span>
          <span style={{ fontWeight: 'bold', fontSize: '0.92em', color: '#ffffff', marginTop: '6px' }}>ห้อง Sterile</span>
          <span style={{ fontSize: '0.72em', color: 'rgba(255,255,255,0.5)' }}>Sterile Storage</span>
          <span style={{ background: '#1abc9c', padding: '3px 10px', borderRadius: '12px', fontSize: '0.85em', fontWeight: 'bold', marginTop: '6px', color: '#fff' }}>{stages.sterile.count} ชิ้น</span>
        </div>

        {/* Stage 2: Clinic */}
        <div
          onClick={() => setSelectedStage('clinic')}
          style={getCentroidLabelStyle(425, 175, selectedStage === 'clinic', 0.35)}
        >
          <span style={{
            fontSize: '2.4em',
            transform: selectedStage === 'clinic' ? 'scale(1.3)' : 'scale(1)',
            transition: 'transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
            display: 'inline-block'
          }}>🦷</span>
          <span style={{ fontWeight: 'bold', fontSize: '0.92em', color: '#ffffff', marginTop: '6px' }}>ห้องคลีนิก</span>
          <span style={{ fontSize: '0.72em', color: 'rgba(255,255,255,0.5)' }}>Clinic Usage</span>
          <span style={{ background: '#f5576c', padding: '3px 10px', borderRadius: '12px', fontSize: '0.85em', fontWeight: 'bold', marginTop: '6px', color: '#fff' }}>{stages.clinic.count} ชิ้น</span>
        </div>

        {/* Stage 3: Washing */}
        <div
          onClick={() => setSelectedStage('washing')}
          style={getCentroidLabelStyle(425, 425, selectedStage === 'washing', 0.5)}
        >
          <span style={{
            fontSize: '2.4em',
            transform: selectedStage === 'washing' ? 'scale(1.3)' : 'scale(1)',
            transition: 'transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
            display: 'inline-block'
          }}>🧽</span>
          <span style={{ fontWeight: 'bold', fontSize: '0.92em', color: '#ffffff', marginTop: '6px' }}>ห้องล้างเครื่องมือ</span>
          <span style={{ fontSize: '0.72em', color: 'rgba(255,255,255,0.5)' }}>Decontamination</span>
          <span style={{ background: '#f39c12', padding: '3px 10px', borderRadius: '12px', fontSize: '0.85em', fontWeight: 'bold', marginTop: '6px', color: '#fff' }}>{stages.washing.count} ชิ้น</span>
        </div>

        {/* Stage 4: Sterilizing */}
        <div
          onClick={() => setSelectedStage('sterilizing')}
          style={getCentroidLabelStyle(175, 425, selectedStage === 'sterilizing', 0.65)}
        >
          <span style={{
            fontSize: '2.4em',
            transform: selectedStage === 'sterilizing' ? 'scale(1.3)' : 'scale(1)',
            transition: 'transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
            display: 'inline-block'
          }}>♨️</span>
          <span style={{ fontWeight: 'bold', fontSize: '0.92em', color: '#ffffff', marginTop: '6px' }}>ห้องนึ่งฆ่าเชื้อ</span>
          <span style={{ fontSize: '0.72em', color: 'rgba(255,255,255,0.5)' }}>กำลังนึ่งฆ่าเชื้อ</span>
          <span style={{ background: '#3498db', padding: '3px 10px', borderRadius: '12px', fontSize: '0.85em', fontWeight: 'bold', marginTop: '6px', color: '#fff' }}>{stages.sterilizing.count} ชิ้น</span>
        </div>
      </div>

      {/* ==================== MOBILE WORKFLOW (LIST) ==================== */}
      <div className="mobile-lifecycle" style={{
        display: 'none',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '15px',
        marginBottom: '30px'
      }}>
        {Object.values(stages).map((stage, idx) => {
          const isSelected = selectedStage === stage.id;
          const stageColors = {
            sterile: '#1abc9c',
            clinic: '#f5576c',
            washing: '#f39c12',
            sterilizing: '#3498db'
          };
          return (
            <React.Fragment key={stage.id}>
              {idx > 0 && <div style={{ fontSize: '1.5em', color: 'rgba(255,255,255,0.4)' }}>⬇️</div>}
              <div
                onClick={() => setSelectedStage(stage.id)}
                style={{
                  width: '100%',
                  maxWidth: '320px',
                  background: isSelected ? 'rgba(255,255,255,0.16)' : 'rgba(255, 255, 255, 0.06)',
                  border: '1px solid rgba(255,255,255,0.15)',
                  borderRadius: '16px',
                  padding: '16px 20px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '15px',
                  cursor: 'pointer',
                  boxShadow: isSelected ? `0 8px 25px ${stage.shadowColor}` : '0 4px 10px rgba(0, 0, 0, 0.2)',
                  transition: 'all 0.2s',
                  animation: `fadeInUp 0.6s ease-out ${0.1 * idx + 0.2}s both`
                }}
              >
                <div style={{
                  width: '45px',
                  height: '45px',
                  borderRadius: '50%',
                  background: stage.gradient,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.4em'
                }}>{stage.icon}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 'bold', fontSize: '0.95em', color: '#ffffff' }}>{stage.title}</div>
                  <div style={{ fontSize: '0.78em', color: 'rgba(255,255,255,0.6)' }}>{stage.subtitle}</div>
                </div>
                <div style={{ fontWeight: 'bold', fontSize: '1.3em', color: stageColors[stage.id] }}>
                  {stage.count}
                </div>
              </div>
            </React.Fragment>
          );
        })}
        <div style={{ fontSize: '1.2em', color: 'rgba(255,255,255,0.4)', marginTop: '5px' }}>🔄 กลับไป ห้อง Sterile</div>
      </div>

      {/* ==================== 📊 USAGE RATE DASHBOARD (TOP 10) ==================== */}
      <div style={{
        background: 'rgba(0,0,0,0.3)',
        borderRadius: '15px',
        border: '1px solid rgba(255,255,255,0.1)',
        padding: '24px',
        marginBottom: '30px',
        animation: 'fadeInUp 0.8s ease-out 0.3s both'
      }}>
        <div style={{ marginBottom: '15px' }}>
          <h3 style={{ margin: 0, fontSize: '1.25em', fontWeight: 'bold', color: '#1abc9c', display: 'flex', alignItems: 'center', gap: '8px' }}>
            📊 อัตราการใช้วัสดุคงทน (10 อันดับแรกที่ถูกใช้งานมากที่สุด)
          </h3>
          <p style={{ color: 'rgba(255,255,255,0.5)', margin: '5px 0 0 0', fontSize: '0.85em' }}>
            จัดอันดับวัสดุคงทนตามจำนวนครั้งที่ถูกเบิกจ่ายออกไปใช้งานที่ห้องคลีนิก{filterDate ? ` ในวันที่ ${filterDate}` : ''}
          </p>
        </div>

        {topUsedItems.length === 0 || topUsedItems.every(i => i.count === 0) ? (
          <div style={{ padding: '30px', textAlign: 'center', color: 'rgba(255,255,255,0.5)', background: 'rgba(255,255,255,0.02)', borderRadius: '12px', fontSize: '0.9em' }}>
            ไม่มีประวัติการเบิกใช้งานพัสดุสำหรับช่วงเวลาที่เลือก
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {topUsedItems.filter(i => i.count > 0).map((item, idx) => {
              const maxCount = Math.max(...topUsedItems.map(i => i.count)) || 1;
              const percentage = (item.count / maxCount) * 100;
              
              // Rank badge styling
              let rankIcon = `${idx + 1}`;
              let rankStyle = {
                background: 'rgba(255,255,255,0.1)',
                color: '#fff'
              };
              if (idx === 0) {
                rankIcon = '🥇';
                rankStyle = { background: 'rgba(241, 196, 15, 0.2)', color: '#f1c405', fontSize: '1.2em' };
              } else if (idx === 1) {
                rankIcon = '🥈';
                rankStyle = { background: 'rgba(189, 195, 199, 0.2)', color: '#bdc3c7', fontSize: '1.2em' };
              } else if (idx === 2) {
                rankIcon = '🥉';
                rankStyle = { background: 'rgba(211, 84, 0, 0.2)', color: '#e67e22', fontSize: '1.2em' };
              }

              return (
                <div
                  key={idx}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '15px',
                    background: 'rgba(255,255,255,0.02)',
                    padding: '12px 18px',
                    borderRadius: '12px',
                    border: '1px solid rgba(255,255,255,0.05)',
                    transition: 'all 0.2s'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
                    e.currentTarget.style.borderColor = 'rgba(26, 188, 156, 0.3)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.background = 'rgba(255,255,255,0.02)';
                    e.currentTarget.style.borderColor = 'rgba(255,255,255,0.05)';
                  }}
                >
                  {/* Rank Badge */}
                  <div style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                    fontSize: '0.9em',
                    ...rankStyle
                  }}>
                    {rankIcon}
                  </div>

                  {/* Thumbnail */}
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    background: 'rgba(0,0,0,0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid rgba(255,255,255,0.05)'
                  }}>
                    {item.master.image_url ? (
                      <img src={item.master.image_url.split('|')[0]} alt={item.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <div style={{ fontSize: '1.2em' }}>📦</div>
                    )}
                  </div>

                  {/* Name and Progress bar */}
                  <div style={{ flex: 1, minWidth: '150px' }}>
                    <div style={{ fontWeight: '600', fontSize: '0.9em', color: '#fff', marginBottom: '6px' }}>
                      {item.name}
                    </div>
                    {/* Progress Bar Track */}
                    <div style={{ width: '100%', height: '8px', background: 'rgba(255,255,255,0.08)', borderRadius: '4px', overflow: 'hidden' }}>
                      {/* Progress Bar Fill */}
                      <div style={{
                        width: `${percentage}%`,
                        height: '100%',
                        background: 'linear-gradient(90deg, #1abc9c 0%, #16a085 100%)',
                        borderRadius: '4px',
                        transition: 'width 0.8s ease-out'
                      }} />
                    </div>
                  </div>

                  {/* Count */}
                  <div style={{ textAlign: 'right', fontWeight: 'bold', color: '#1abc9c', fontSize: '1em', minWidth: '80px' }}>
                    เบิก {item.count} ครั้ง
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ==================== DETAIL TABLE SECTION ==================== */}
      <div style={{
        background: 'rgba(0,0,0,0.3)',
        borderRadius: '15px',
        border: '1px solid rgba(255,255,255,0.1)',
        padding: '24px',
        animation: 'fadeInUp 0.8s ease-out 0.4s both'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px',
          flexWrap: 'wrap',
          gap: '10px'
        }}>
          <h3 style={{ margin: 0, fontSize: '1.25em', fontWeight: 'bold', color: '#1abc9c', display: 'flex', alignItems: 'center', gap: '8px' }}>
            📋 รายละเอียดรายการใน: {getStageTitle(selectedStage)}
          </h3>

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '0.85em', color: 'rgba(255,255,255,0.6)' }}>กรองตามวันที่:</span>
            <input
              type="date"
              value={filterDate}
              onChange={(e) => setFilterDate(e.target.value)}
              style={{
                background: 'rgba(255,255,255,0.08)',
                border: '1px solid rgba(255,255,255,0.15)',
                borderRadius: '8px',
                color: '#fff',
                padding: '6px 10px',
                fontSize: '0.85em',
                outline: 'none',
                cursor: 'pointer'
              }}
            />
            <button
              onClick={() => {
                const today = new Date();
                const yyyy = today.getFullYear();
                const mm = String(today.getMonth() + 1).padStart(2, '0');
                const dd = String(today.getDate()).padStart(2, '0');
                setFilterDate(`${yyyy}-${mm}-${dd}`);
              }}
              style={{
                background: 'rgba(26, 188, 156, 0.2)',
                border: '1px solid rgba(26, 188, 156, 0.4)',
                color: '#1abc9c',
                padding: '6px 12px',
                borderRadius: '8px',
                fontSize: '0.85em',
                fontWeight: 'bold',
                cursor: 'pointer',
                transition: 'background 0.2s'
              }}
              onMouseOver={(e) => e.currentTarget.style.background = 'rgba(26, 188, 156, 0.3)'}
              onMouseOut={(e) => e.currentTarget.style.background = 'rgba(26, 188, 156, 0.2)'}
            >
              📅 วันปัจจุบัน
            </button>
            {filterDate && (
              <button
                onClick={() => setFilterDate('')}
                style={{
                  background: 'rgba(255, 255, 255, 0.08)',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  color: '#fff',
                  padding: '6px 12px',
                  borderRadius: '8px',
                  fontSize: '0.85em',
                  cursor: 'pointer',
                  transition: 'background 0.2s'
                }}
                onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)'}
                onMouseOut={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)'}
              >
                ทั้งหมด
              </button>
            )}
          </div>

          <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontSize: '0.85em', background: 'rgba(46, 204, 113, 0.15)', border: '1px solid rgba(46, 204, 113, 0.3)', padding: '6px 12px', borderRadius: '12px', color: '#2ecc71', fontWeight: '600' }}>
              📥 รับเข้า: {stageTotalStats.in} ชิ้น
            </span>
            <span style={{ fontSize: '0.85em', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.15)', padding: '6px 12px', borderRadius: '12px', color: '#ffffff', fontWeight: '600' }}>
              📦 จำนวนทั้งหมด: {stageTotalStats.total} ชิ้น
            </span>
            <span style={{ fontSize: '0.85em', background: 'rgba(231, 76, 60, 0.15)', border: '1px solid rgba(231, 76, 60, 0.3)', padding: '6px 12px', borderRadius: '12px', color: '#e74c3c', fontWeight: '600' }}>
              📤 ส่งออก: {stageTotalStats.out} ชิ้น
            </span>
          </div>
        </div>

        {/* Dynamic Details Rendering */}
        {selectedStage === 'sterile' && (() => {
          // Grouping sterileItems by master_id
          const groupedSterile = sterileItemsFiltered.reduce((acc, item) => {
            const mId = item.master_id;
            if (!acc[mId]) {
              acc[mId] = {
                master: durableMasters.find(m => m.id === mId) || { id: mId, item_name: item.item_name || 'ไม่ทราบชื่อ', image_url: '' },
                items: []
              };
            }
            acc[mId].items.push(item);
            return acc;
          }, {});
          const groupedSterileList = Object.values(groupedSterile);

          if (sterileItemsFiltered.length === 0) {
            return (
              <div style={{ padding: '30px', textAlign: 'center', color: 'rgba(255,255,255,0.5)', background: 'rgba(255,255,255,0.02)', borderRadius: '12px' }}>
                ไม่มีข้อมูลอุปกรณ์คงทนในห้อง Sterile
              </div>
            );
          }

          return (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
              gap: '20px',
              marginTop: '10px',
              animation: 'fadeInUp 0.6s ease-out both'
            }}>
              {groupedSterileList.map(({ master, items }) => (
                <div
                  key={master.id}
                  onClick={() => setSelectedItemDetail({ master, items })}
                  style={{
                    position: 'relative',
                    borderRadius: '16px',
                    border: '1px solid rgba(255,255,255,0.1)',
                    background: 'rgba(255,255,255,0.05)',
                    cursor: 'pointer',
                    overflow: 'hidden',
                    transition: 'all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    padding: '15px',
                    boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-5px)';
                    e.currentTarget.style.boxShadow = '0 8px 25px rgba(26, 188, 156, 0.3)';
                    e.currentTarget.style.borderColor = 'rgba(26, 188, 156, 0.4)';
                    e.currentTarget.style.background = 'rgba(255,255,255,0.08)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.2)';
                    e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)';
                    e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
                  }}
                >
                  {/* Image container */}
                  <div style={{
                    position: 'relative',
                    width: '100%',
                    height: '140px',
                    borderRadius: '10px',
                    overflow: 'hidden',
                    background: 'rgba(0,0,0,0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '12px',
                    border: '1px solid rgba(255,255,255,0.05)'
                  }}>
                    {master.image_url ? (
                      <img
                        src={master.image_url.split('|')[0]}
                        alt={master.item_name}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      <div style={{
                        fontSize: '3em',
                        background: 'linear-gradient(135deg, #37ecba 0%, #72afd3 100%)',
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        opacity: 0.8
                      }}>
                        📦
                      </div>
                    )}

                    {/* Overlaid Count Badge (Layer) */}
                    <div style={{
                      position: 'absolute',
                      top: '10px',
                      right: '10px',
                      background: '#1abc9c',
                      color: '#ffffff',
                      padding: '4px 10px',
                      borderRadius: '20px',
                      fontSize: '0.85em',
                      fontWeight: 'bold',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                      zIndex: 2
                    }}>
                      {items.length} ชิ้น
                    </div>
                  </div>

                  {/* Item Name */}
                  <div style={{
                    fontWeight: '600',
                    fontSize: '0.95em',
                    color: '#ffffff',
                    textAlign: 'center',
                    width: '100%',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}>
                    {master.item_name}
                  </div>
                  {(() => {
                    const stats = getItemLogStats(master.item_name);
                    return (
                      <div style={{
                        fontSize: '0.78em',
                        color: 'rgba(255,255,255,0.5)',
                        marginTop: '4px',
                        textAlign: 'center',
                        display: 'flex',
                        gap: '8px',
                        justifyContent: 'center'
                      }}>
                        <span style={{ color: '#2ecc71' }}>📥 รับ: {stats.sterile.in}</span>
                        <span style={{ color: 'rgba(255,255,255,0.4)' }}>|</span>
                        <span style={{ color: '#e74c3c' }}>📤 ส่ง: {stats.sterile.out}</span>
                      </div>
                    );
                  })()}
                </div>
              ))}
            </div>
          );
        })()}

        {selectedStage === 'clinic' && (() => {
          // Grouping clinicItems by master_id
          const groupedClinic = clinicItemsFiltered.reduce((acc, item) => {
            const mId = item.master_id;
            if (!acc[mId]) {
              acc[mId] = {
                master: durableMasters.find(m => m.id === mId) || { id: mId, item_name: item.item_name || 'ไม่ทราบชื่อ', image_url: '' },
                items: []
              };
            }
            acc[mId].items.push(item);
            return acc;
          }, {});
          const groupedClinicList = Object.values(groupedClinic);

          if (clinicItemsFiltered.length === 0) {
            return (
              <div style={{ padding: '30px', textAlign: 'center', color: 'rgba(255,255,255,0.5)', background: 'rgba(255,255,255,0.02)', borderRadius: '12px' }}>
                ไม่มีข้อมูลอุปกรณ์คงทนถูกจ่ายเข้าห้องคลีนิก
              </div>
            );
          }

          return (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
              gap: '20px',
              marginTop: '10px',
              animation: 'fadeInUp 0.6s ease-out both'
            }}>
              {groupedClinicList.map(({ master, items }) => (
                <div
                  key={master.id}
                  onClick={() => setSelectedClinicItemDetail({ master, items })}
                  style={{
                    position: 'relative',
                    borderRadius: '16px',
                    border: '1px solid rgba(255,255,255,0.1)',
                    background: 'rgba(255,255,255,0.05)',
                    cursor: 'pointer',
                    overflow: 'hidden',
                    transition: 'all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    padding: '15px',
                    boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-5px)';
                    e.currentTarget.style.boxShadow = '0 8px 25px rgba(245, 87, 108, 0.3)';
                    e.currentTarget.style.borderColor = 'rgba(245, 87, 108, 0.4)';
                    e.currentTarget.style.background = 'rgba(255,255,255,0.08)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.2)';
                    e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)';
                    e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
                  }}
                >
                  {/* Image container */}
                  <div style={{
                    position: 'relative',
                    width: '100%',
                    height: '140px',
                    borderRadius: '10px',
                    overflow: 'hidden',
                    background: 'rgba(0,0,0,0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '12px',
                    border: '1px solid rgba(255,255,255,0.05)'
                  }}>
                    {master.image_url ? (
                      <img
                        src={master.image_url.split('|')[0]}
                        alt={master.item_name}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      <div style={{
                        fontSize: '3em',
                        background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        opacity: 0.8
                      }}>
                        📦
                      </div>
                    )}

                    {/* Overlaid Count Badge (Layer) */}
                    <div style={{
                      position: 'absolute',
                      top: '10px',
                      right: '10px',
                      background: '#f5576c',
                      color: '#ffffff',
                      padding: '4px 10px',
                      borderRadius: '20px',
                      fontSize: '0.85em',
                      fontWeight: 'bold',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                      zIndex: 2
                    }}>
                      {items.length} ชิ้น
                    </div>
                  </div>

                  {/* Item Name */}
                  <div style={{
                    fontWeight: '600',
                    fontSize: '0.95em',
                    color: '#ffffff',
                    textAlign: 'center',
                    width: '100%',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}>
                    {master.item_name}
                  </div>
                  {(() => {
                    const stats = getItemLogStats(master.item_name);
                    return (
                      <div style={{
                        fontSize: '0.78em',
                        color: 'rgba(255,255,255,0.5)',
                        marginTop: '4px',
                        textAlign: 'center',
                        display: 'flex',
                        gap: '8px',
                        justifyContent: 'center'
                      }}>
                        <span style={{ color: '#2ecc71' }}>📥 รับ: {stats.clinic.in}</span>
                        <span style={{ color: 'rgba(255,255,255,0.4)' }}>|</span>
                        <span style={{ color: '#e74c3c' }}>📤 ส่ง: {stats.clinic.out}</span>
                      </div>
                    );
                  })()}
                </div>
              ))}
            </div>
          );
        })()}

        {selectedStage === 'washing' && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9em', textAlign: 'left', color: '#ffffff' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.8)' }}>
                  <th style={{ padding: '12px 10px' }}>#</th>
                  <th style={{ padding: '12px 10px' }}>วันที่รับคืน</th>
                  <th style={{ padding: '12px 10px' }}>ชื่อวัสดุคงทน</th>
                  <th style={{ padding: '12px 10px' }}>จำนวน</th>
                  <th style={{ padding: '12px 10px' }}>ผู้สแกนเข้าระบบ</th>
                  <th style={{ padding: '12px 10px', textAlign: 'center' }}>การจัดการ</th>
                </tr>
              </thead>
              <tbody>
                {groupedWashingItemsFiltered.length === 0 ? (
                  <tr><td colSpan="6" style={{ padding: '20px', textAlign: 'center', color: 'rgba(255,255,255,0.5)' }}>ไม่มีรายการวัสดุคงทนค้างอยู่ในห้องล้างเครื่องมือ</td></tr>
                ) : (
                  groupedWashingItemsFiltered.map((group, idx) => {
                    return (
                      <tr key={idx} style={{ borderBottom: '1px solid rgba(255,255,255,0.1)', color: '#ffffff' }}>
                        <td style={{ padding: '10px' }}>{idx + 1}</td>
                        <td style={{ padding: '10px', color: 'rgba(255,255,255,0.7)' }}>{group.date}</td>
                        <td style={{ padding: '10px', fontWeight: '500' }}>{group.item_name}</td>
                        <td style={{ padding: '10px', fontSize: '1.1em', color: '#f39c12', fontWeight: 'bold' }}>{group.items.length} ชิ้น</td>
                        <td style={{ padding: '10px', color: 'rgba(255,255,255,0.8)' }}>
                          {group.scanners.map((s, sIdx) => (
                            <span key={sIdx} style={{ background: 'rgba(255,255,255,0.1)', padding: '2px 8px', borderRadius: '8px', fontSize: '0.85em', marginRight: '5px', display: 'inline-block' }}>
                              👤 {s}
                            </span>
                          ))}
                        </td>
                        <td style={{ padding: '10px', textAlign: 'center' }}>
                          <button
                            onClick={() => setSelectedWashingDetail(group)}
                            style={{
                              background: 'linear-gradient(135deg, #f39c12 0%, #d35400 100%)',
                              border: 'none',
                              color: '#fff',
                              padding: '6px 12px',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              fontWeight: '600',
                              fontSize: '0.85em',
                              boxShadow: '0 4px 10px rgba(243, 156, 18, 0.3)',
                              transition: 'transform 0.2s, box-shadow 0.2s'
                            }}
                            onMouseOver={(e) => {
                              e.currentTarget.style.transform = 'translateY(-2px)';
                              e.currentTarget.style.boxShadow = '0 6px 15px rgba(243, 156, 18, 0.4)';
                            }}
                            onMouseOut={(e) => {
                              e.currentTarget.style.transform = 'translateY(0)';
                              e.currentTarget.style.boxShadow = '0 4px 10px rgba(243, 156, 18, 0.3)';
                            }}
                          >
                            👁️ ดูรายละเอียด
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}

        {selectedStage === 'sterilizing' && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9em', textAlign: 'left', color: '#ffffff' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.8)' }}>
                  <th style={{ padding: '12px 10px' }}>#</th>
                  <th style={{ padding: '12px 10px' }}>บาร์โค้ด</th>
                  <th style={{ padding: '12px 10px' }}>ชื่อวัสดุ</th>
                  <th style={{ padding: '12px 10px' }}>เครื่องนึ่งฆ่าเชื้อ</th>
                  <th style={{ padding: '12px 10px' }}>รอบการนึ่ง</th>
                  <th style={{ padding: '12px 10px' }}>ผู้รับผิดชอบ</th>
                  <th style={{ padding: '12px 10px' }}>เวลาส่งแพ็ก</th>
                </tr>
              </thead>
              <tbody>
                {sterilizingItemsFiltered.length === 0 ? (
                  <tr><td colSpan="7" style={{ padding: '20px', textAlign: 'center', color: 'rgba(255,255,255,0.5)' }}>ไม่มีอุปกรณ์คงทนกำลังนึ่งอยู่ในเครื่องนึ่ง</td></tr>
                ) : (
                  sterilizingItemsFiltered.map((item, idx) => (
                    <tr key={item.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.1)', color: '#ffffff' }}>
                      <td style={{ padding: '10px' }}>{idx + 1}</td>
                      <td style={{ padding: '10px', fontFamily: 'monospace', color: '#3498db', fontWeight: 'bold' }}>{item.unique_barcode}</td>
                      <td style={{ padding: '10px', fontWeight: '500' }}>{item.item_name}</td>
                      <td style={{ padding: '10px', color: '#1abc9c', fontWeight: 'bold' }}>{item.details?.machine ? `เครื่องที่ ${item.details.machine}` : '-'}</td>
                      <td style={{ padding: '10px' }}>{item.details?.cycle ? `รอบที่ ${item.details.cycle}` : '-'}</td>
                      <td style={{ padding: '10px', fontSize: '0.85em' }}>{item.details?.preparedBy ? `เตรียม: ${item.details.preparedBy} / ตรวจ: ${item.details.checkedBy}` : '-'}</td>
                      <td style={{ padding: '10px', color: 'rgba(255,255,255,0.6)', fontSize: '0.85em' }}>{formatTimeThai(item.last_updated)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Detail Modal Overlay for Sterile Items */}
      {selectedItemDetail && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(15, 23, 42, 0.75)',
          backdropFilter: 'blur(10px)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 9999,
          padding: '20px'
        }}>
          <div style={{
            background: '#1f2937',
            borderRadius: '24px',
            border: '1px solid rgba(255,255,255,0.1)',
            width: '95%',
            maxWidth: '900px',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
            padding: '30px',
            position: 'relative',
            textAlign: 'left'
          }}>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px', borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: '15px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                {selectedItemDetail.master.image_url ? (
                  <img src={selectedItemDetail.master.image_url.split('|')[0]} alt={selectedItemDetail.master.item_name} style={{ width: '60px', height: '60px', borderRadius: '12px', objectFit: 'cover', border: '1px solid rgba(255,255,255,0.1)' }} />
                ) : (
                  <div style={{ width: '60px', height: '60px', borderRadius: '12px', background: 'linear-gradient(135deg, #37ecba 0%, #72afd3 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.8em' }}>📦</div>
                )}
                <div>
                  <h3 style={{ margin: 0, fontSize: '1.35em', fontWeight: 'bold', color: '#1abc9c' }}>{selectedItemDetail.master.item_name}</h3>
                  <span style={{ fontSize: '0.85em', color: 'rgba(255,255,255,0.5)' }}>รหัสวัสดุ: {selectedItemDetail.master.id} | สต็อก Sterile: {selectedItemDetail.items.length} ชิ้น</span>
                </div>
              </div>
              <button
                onClick={() => setSelectedItemDetail(null)}
                style={{
                  background: 'rgba(255,255,255,0.1)',
                  border: 'none',
                  color: '#fff',
                  borderRadius: '50%',
                  width: '36px',
                  height: '36px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.2em',
                  transition: 'background 0.2s'
                }}
              >
                ✕
              </button>
            </div>

            {/* Barcode Table */}
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9em', textAlign: 'left', color: '#ffffff' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.7)' }}>
                    <th style={{ padding: '12px 10px' }}>บาร์โค้ด</th>
                    <th style={{ padding: '12px 10px' }}>วันที่ผลิต</th>
                    <th style={{ padding: '12px 10px' }}>วันหมดอายุ</th>
                    <th style={{ padding: '12px 10px' }}>วันคงเหลือ</th>
                    <th style={{ padding: '12px 10px' }}>ผู้เตรียม/ผู้ตรวจ</th>
                    <th style={{ padding: '12px 10px' }}>เครื่อง/รอบ</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedItemDetail.items.map((item) => {
                    const daysLeft = getDaysRemaining(item.details?.expDate);
                    let daysLeftDisplay = '-';
                    let daysLeftStyle = { color: 'rgba(255,255,255,0.7)' };

                    if (daysLeft !== null) {
                      if (daysLeft > 10) {
                        daysLeftDisplay = `🟢 เหลือ ${daysLeft} วัน`;
                        daysLeftStyle = { color: '#2ecc71', fontWeight: '600' };
                      } else if (daysLeft > 0) {
                        daysLeftDisplay = `🟡 เหลือ ${daysLeft} วัน`;
                        daysLeftStyle = { color: '#f1c40f', fontWeight: '600' };
                      } else if (daysLeft === 0) {
                        daysLeftDisplay = `🚨 หมดอายุวันนี้`;
                        daysLeftStyle = { color: '#e67e22', fontWeight: 'bold' };
                      } else {
                        daysLeftDisplay = `🔴 หมดอายุแล้ว ${Math.abs(daysLeft)} วัน`;
                        daysLeftStyle = { color: '#e74c3c', fontWeight: 'bold' };
                      }
                    }

                    return (
                      <tr key={item.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                        <td style={{ padding: '12px 10px', fontFamily: 'monospace', color: '#1abc9c', fontWeight: 'bold' }}>{item.unique_barcode}</td>
                        <td style={{ padding: '12px 10px' }}>{item.details?.mfgDate ? formatDateThai(item.details.mfgDate) : '-'}</td>
                        <td style={{ padding: '12px 10px' }}>{item.details?.expDate ? formatDateThai(item.details.expDate) : '-'}</td>
                        <td style={{ padding: '12px 10px', ...daysLeftStyle }}>{daysLeftDisplay}</td>
                        <td style={{ padding: '12px 10px', fontSize: '0.85em' }}>{item.details?.preparedBy || '-'}/{item.details?.checkedBy || '-'}</td>
                        <td style={{ padding: '12px 10px' }}>{item.details?.machine ? `เครื่อง ${item.details.machine}` : '-'} / {item.details?.cycle ? `รอบ ${item.details.cycle}` : '-'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div style={{ marginTop: '25px', display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setSelectedItemDetail(null)}
                style={{
                  padding: '10px 24px',
                  background: '#1abc9c',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  boxShadow: '0 4px 12px rgba(26,188,156,0.2)',
                  transition: 'all 0.2s'
                }}
              >
                ปิดหน้าต่าง
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tracking locations modal for Clinic items */}
      {selectedClinicItemDetail && (() => {
        const itemsByLocation = selectedClinicItemDetail.items.reduce((acc, item) => {
          const loc = item.location || 'ไม่ระบุห้อง';
          if (!acc[loc]) {
            acc[loc] = [];
          }
          acc[loc].push(item);
          return acc;
        }, {});

        return (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.75)',
            backdropFilter: 'blur(10px)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999,
            padding: '20px'
          }}>
            <div style={{
              background: '#1f2937',
              borderRadius: '24px',
              border: '1px solid rgba(255,255,255,0.1)',
              width: '95%',
              maxWidth: '700px',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
              padding: '30px',
              position: 'relative',
              textAlign: 'left'
            }}>
              {/* Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px', borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: '15px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                  {selectedClinicItemDetail.master.image_url ? (
                    <img src={selectedClinicItemDetail.master.image_url.split('|')[0]} alt={selectedClinicItemDetail.master.item_name} style={{ width: '60px', height: '60px', borderRadius: '12px', objectFit: 'cover', border: '1px solid rgba(255,255,255,0.1)' }} />
                  ) : (
                    <div style={{ width: '60px', height: '60px', borderRadius: '12px', background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.8em' }}>📦</div>
                  )}
                  <div>
                    <h3 style={{ margin: 0, fontSize: '1.35em', fontWeight: 'bold', color: '#f5576c' }}>{selectedClinicItemDetail.master.item_name}</h3>
                    <span style={{ fontSize: '0.85em', color: 'rgba(255,255,255,0.5)' }}>รหัสวัสดุ: {selectedClinicItemDetail.master.id} | ยอดรวมในห้องคลีนิก: {selectedClinicItemDetail.items.length} ชิ้น</span>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedClinicItemDetail(null)}
                  style={{
                    background: 'rgba(255,255,255,0.1)',
                    border: 'none',
                    color: '#fff',
                    borderRadius: '50%',
                    width: '36px',
                    height: '36px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.2em'
                  }}
                >
                  ✕
                </button>
              </div>

              <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.92em', marginBottom: '20px' }}>
                📍 แสดงรายชื่อห้องคลีนิกที่มีวัสดุชิ้นนี้อยู่และจำนวนสต็อก:
              </p>

              {/* Locations List */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {Object.entries(itemsByLocation).map(([locationName, locItems]) => (
                  <div key={locationName} style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    background: 'rgba(255,255,255,0.03)',
                    padding: '16px 20px',
                    borderRadius: '16px',
                    border: '1px solid rgba(255,255,255,0.06)'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <span style={{ fontSize: '1.3em' }}>📍</span>
                      <div>
                        <div style={{ fontWeight: 'bold', color: '#ffffff', fontSize: '1.05em' }}>{locationName}</div>
                        <span style={{ fontSize: '0.82em', color: '#f5576c', fontWeight: '600' }}>จำนวน: {locItems.length} ชิ้น</span>
                      </div>
                    </div>
                    <button
                      onClick={() => setSelectedLocationDetail({ location: locationName, items: locItems })}
                      style={{
                        padding: '8px 16px',
                        background: '#f5576c',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontWeight: 'bold',
                        fontSize: '0.88em',
                        boxShadow: '0 4px 12px rgba(245,87,108,0.2)',
                        transition: 'background 0.2s'
                      }}
                    >
                      🔎 ดูรายละเอียดในห้องนี้
                    </button>
                  </div>
                ))}
              </div>

              <div style={{ marginTop: '30px', display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setSelectedClinicItemDetail(null)}
                  style={{
                    padding: '10px 24px',
                    background: 'rgba(255,255,255,0.1)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    transition: 'background 0.2s'
                  }}
                >
                  ปิดหน้าต่าง
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Sub-modal: Details inside a specific Location */}
      {selectedLocationDetail && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(15, 23, 42, 0.85)',
          backdropFilter: 'blur(8px)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 10000,
          padding: '20px'
        }}>
          <div style={{
            background: '#1f2937',
            borderRadius: '24px',
            border: '1px solid rgba(255,255,255,0.1)',
            width: '90%',
            maxWidth: '800px',
            maxHeight: '85vh',
            overflowY: 'auto',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.6)',
            padding: '30px',
            position: 'relative',
            textAlign: 'left'
          }}>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: '15px' }}>
              <div>
                <h4 style={{ margin: 0, fontSize: '1.2em', color: '#f5576c', fontWeight: 'bold' }}>📍 {selectedLocationDetail.location}</h4>
                <span style={{ fontSize: '0.85em', color: 'rgba(255,255,255,0.5)' }}>รายละเอียดวัสดุคงทน: {selectedClinicItemDetail?.master?.item_name || selectedItemDetail?.master?.item_name} ({selectedLocationDetail.items.length} ชิ้น)</span>
              </div>
              <button
                onClick={() => setSelectedLocationDetail(null)}
                style={{
                  background: 'rgba(255,255,255,0.1)',
                  border: 'none',
                  color: '#fff',
                  borderRadius: '50%',
                  width: '32px',
                  height: '32px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1em'
                }}
              >
                ✕
              </button>
            </div>

            {/* Barcode Table */}
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9em', textAlign: 'left', color: '#ffffff' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.7)' }}>
                    <th style={{ padding: '12px 10px' }}>บาร์โค้ด</th>
                    <th style={{ padding: '12px 10px' }}>วันที่ผลิต</th>
                    <th style={{ padding: '12px 10px' }}>วันหมดอายุ</th>
                    <th style={{ padding: '12px 10px' }}>วันคงเหลือ</th>
                    <th style={{ padding: '12px 10px' }}>ผู้เตรียม/ผู้ตรวจ</th>
                    <th style={{ padding: '12px 10px' }}>เครื่อง/รอบ</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedLocationDetail.items.map((item) => {
                    const daysLeft = getDaysRemaining(item.details?.expDate);
                    let daysLeftDisplay = '-';
                    let daysLeftStyle = { color: 'rgba(255,255,255,0.7)' };

                    if (daysLeft !== null) {
                      if (daysLeft > 10) {
                        daysLeftDisplay = `🟢 เหลือ ${daysLeft} วัน`;
                        daysLeftStyle = { color: '#2ecc71', fontWeight: '600' };
                      } else if (daysLeft > 0) {
                        daysLeftDisplay = `🟡 เหลือ ${daysLeft} วัน`;
                        daysLeftStyle = { color: '#f1c40f', fontWeight: '600' };
                      } else if (daysLeft === 0) {
                        daysLeftDisplay = `🚨 หมดอายุวันนี้`;
                        daysLeftStyle = { color: '#e67e22', fontWeight: 'bold' };
                      } else {
                        daysLeftDisplay = `🔴 หมดอายุแล้ว ${Math.abs(daysLeft)} วัน`;
                        daysLeftStyle = { color: '#e74c3c', fontWeight: 'bold' };
                      }
                    }

                    return (
                      <tr key={item.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                        <td style={{ padding: '12px 10px', fontFamily: 'monospace', color: '#f5576c', fontWeight: 'bold' }}>{item.unique_barcode}</td>
                        <td style={{ padding: '12px 10px' }}>{item.details?.mfgDate ? formatDateThai(item.details.mfgDate) : '-'}</td>
                        <td style={{ padding: '12px 10px' }}>{item.details?.expDate ? formatDateThai(item.details.expDate) : '-'}</td>
                        <td style={{ padding: '12px 10px', ...daysLeftStyle }}>{daysLeftDisplay}</td>
                        <td style={{ padding: '12px 10px', fontSize: '0.85em' }}>{item.details?.preparedBy || '-'}/{item.details?.checkedBy || '-'}</td>
                        <td style={{ padding: '12px 10px' }}>{item.details?.machine ? `เครื่อง ${item.details.machine}` : '-'} / {item.details?.cycle ? `รอบ ${item.details.cycle}` : '-'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div style={{ marginTop: '25px', display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setSelectedLocationDetail(null)}
                style={{
                  padding: '10px 24px',
                  background: '#f5576c',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: 'bold'
                }}
              >
                ย้อนกลับ
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal Overlay for Washing Items */}
      {selectedWashingDetail && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(15, 23, 42, 0.75)',
          backdropFilter: 'blur(10px)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 9999,
          padding: '20px'
        }}>
          <div style={{
            background: '#1f2937',
            borderRadius: '24px',
            border: '1px solid rgba(255,255,255,0.1)',
            width: '95%',
            maxWidth: '1000px',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
            padding: '30px',
            position: 'relative',
            textAlign: 'left'
          }}>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px', borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: '15px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                {selectedWashingDetail.master.image_url ? (
                  <img src={selectedWashingDetail.master.image_url.split('|')[0]} alt={selectedWashingDetail.item_name} style={{ width: '60px', height: '60px', borderRadius: '12px', objectFit: 'cover', border: '1px solid rgba(255,255,255,0.1)' }} />
                ) : (
                  <div style={{ width: '60px', height: '60px', borderRadius: '12px', background: 'linear-gradient(135deg, #f39c12 0%, #d35400 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.8em' }}>🧼</div>
                )}
                <div>
                  <h3 style={{ margin: 0, fontSize: '1.35em', fontWeight: 'bold', color: '#f39c12' }}>{selectedWashingDetail.item_name}</h3>
                  <span style={{ fontSize: '0.85em', color: 'rgba(255,255,255,0.5)' }}>วันที่รับคืน: {selectedWashingDetail.date} | ทั้งหมด: {selectedWashingDetail.items.length} ชิ้น</span>
                </div>
              </div>
              <button
                onClick={() => setSelectedWashingDetail(null)}
                style={{
                  background: 'rgba(255,255,255,0.1)',
                  border: 'none',
                  color: '#fff',
                  borderRadius: '50%',
                  width: '36px',
                  height: '36px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.2em',
                  transition: 'background 0.2s'
                }}
              >
                ✕
              </button>
            </div>

            {/* Items Table */}
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9em', textAlign: 'left', color: '#ffffff' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.7)' }}>
                    <th style={{ padding: '12px 10px' }}>#</th>
                    <th style={{ padding: '12px 10px' }}>บาร์โค้ด</th>
                    <th style={{ padding: '12px 10px' }}>วันที่ผลิต</th>
                    <th style={{ padding: '12px 10px' }}>วันที่เข้าห้องล้าง</th>
                    <th style={{ padding: '12px 10px' }}>รวมวันที่อยู่ในระบบ</th>
                    <th style={{ padding: '12px 10px' }}>ผู้เตรียม</th>
                    <th style={{ padding: '12px 10px' }}>ผู้ตรวจ</th>
                    <th style={{ padding: '12px 10px' }}>เครื่อง</th>
                    <th style={{ padding: '12px 10px' }}>รอบ</th>
                    <th style={{ padding: '12px 10px' }}>ส่งมาจากห้องไหน</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedWashingDetail.items.map((item, idx) => {
                    let daysInSystemDisplay = '-';
                    let daysInSystemStyle = { color: 'rgba(255,255,255,0.7)' };

                    if (item.mfgDate && item.mfgDate !== '-' && item.timestamp && item.timestamp !== '-') {
                      const mfg = new Date(item.mfgDate);
                      const wash = new Date(item.timestamp);
                      mfg.setHours(0, 0, 0, 0);
                      wash.setHours(0, 0, 0, 0);
                      const diffTime = wash - mfg;
                      const days = Math.round(diffTime / (1000 * 60 * 60 * 24));
                      const safeDays = days >= 0 ? days : 0;
                      daysInSystemDisplay = `${safeDays} วัน`;
                    }

                    if (item.expDate && item.expDate !== '-' && item.timestamp && item.timestamp !== '-') {
                      const exp = new Date(item.expDate);
                      const wash = new Date(item.timestamp);
                      exp.setHours(0, 0, 0, 0);
                      wash.setHours(0, 0, 0, 0);
                      const remainingTime = exp - wash;
                      const remainingDays = Math.round(remainingTime / (1000 * 60 * 60 * 24));

                      if (remainingDays < 0) {
                        daysInSystemStyle = { color: '#e74c3c', fontWeight: 'bold' };
                      } else if (remainingDays >= 0 && remainingDays <= 3) {
                        daysInSystemStyle = { color: '#f1c40f', fontWeight: '600' };
                      } else {
                        daysInSystemStyle = { color: '#2ecc71', fontWeight: '600' };
                      }
                    }

                    const origin = item.location || 'ไม่ระบุ';
                    const isFromSterile = origin.toLowerCase().includes('sterile');
                    const originStyle = isFromSterile
                      ? { color: '#ff4d4d', fontWeight: 'bold', background: 'rgba(255, 77, 77, 0.15)', padding: '4px 8px', borderRadius: '6px', display: 'inline-block' }
                      : { color: 'rgba(255,255,255,0.9)' };

                    return (
                      <tr key={idx} style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                        <td style={{ padding: '12px 10px' }}>{idx + 1}</td>
                        <td style={{ padding: '12px 10px', fontFamily: 'monospace', color: '#f39c12', fontWeight: 'bold' }}>{item.barcode}</td>
                        <td style={{ padding: '12px 10px' }}>{item.mfgDate !== '-' ? formatDateThai(item.mfgDate) : '-'}</td>
                        <td style={{ padding: '12px 10px' }}>{item.timestamp && item.timestamp !== '-' ? formatTimeThai(item.timestamp) : '-'}</td>
                        <td style={{ padding: '12px 10px', ...daysInSystemStyle }}>{daysInSystemDisplay}</td>
                        <td style={{ padding: '12px 10px' }}>{item.preparedBy}</td>
                        <td style={{ padding: '12px 10px' }}>{item.checkedBy}</td>
                        <td style={{ padding: '12px 10px' }}>{item.machine !== '-' ? `เครื่อง ${item.machine}` : '-'}</td>
                        <td style={{ padding: '12px 10px' }}>{item.cycle !== '-' ? `รอบ ${item.cycle}` : '-'}</td>
                        <td style={{ padding: '12px 10px' }}>
                          <span style={originStyle}>
                            {isFromSterile ? '🚨 ' : ''}{origin.replace(/^\s*-\s*/, '')}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div style={{ marginTop: '25px', display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setSelectedWashingDetail(null)}
                style={{
                  padding: '10px 24px',
                  background: '#f39c12',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  boxShadow: '0 4px 10px rgba(243, 156, 18, 0.3)',
                  transition: 'background 0.2s'
                }}
                onMouseOver={(e) => e.currentTarget.style.background = '#d35400'}
                onMouseOut={(e) => e.currentTarget.style.background = '#f39c12'}
              >
                ย้อนกลับ
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Styled css overrides for responsive diamond layout & rotation animation */}
      <style>{`
        .desktop-lifecycle {
          display: block;
        }
        .mobile-lifecycle {
          display: none;
        }
        @keyframes spin {
          0% { transform: translate(-50%, -50%) rotate(0deg); }
          100% { transform: translate(-50%, -50%) rotate(360deg); }
        }
        @keyframes fadeInDown {
          from { opacity: 0; transform: translateY(-20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeInScale {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
        @keyframes fadeInCentroid {
          from { opacity: 0; transform: translate(-50%, -50%) scale(0.9); }
          to { opacity: 1; transform: translate(-50%, -50%) scale(1); }
        }
        @media (max-width: 767px) {
          .desktop-lifecycle {
            display: none !important;
          }
          .mobile-lifecycle {
            display: flex !important;
          }
        }
      `}</style>
    </div>
  );
};

// ตำแหน่งและสไตล์ของป้ายระบุข้อมูลแต่ละขั้นตอน
const getCentroidLabelStyle = (left, top, isSelected, delay = 0) => ({
  position: 'absolute',
  left: `${left}px`,
  top: `${top}px`,
  transform: 'translate(-50%, -50%)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 3,
  cursor: 'pointer',
  textAlign: 'center',
  padding: '12px',
  borderRadius: '16px',
  transition: 'all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
  background: isSelected ? 'rgba(255, 255, 255, 0.08)' : 'transparent',
  border: isSelected ? '1px solid rgba(255, 255, 255, 0.15)' : '1px solid transparent',
  boxShadow: isSelected ? '0 8px 25px rgba(0,0,0,0.3)' : 'none',
  width: '160px',
  height: '140px',
  boxSizing: 'border-box',
  userSelect: 'none',
  animation: `fadeInCentroid 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94) ${delay}s both`
});



// ฟังก์ชันแปลงเวลา
const formatTimeThai = (isoStr) => {
  if (!isoStr) return '-';
  const d = new Date(isoStr);
  return d.toLocaleDateString('th-TH', {
    day: '2-digit', month: '2-digit', year: 'numeric'
  }) + ' ' + d.toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' }) + ' น.';
};

const formatDateThai = (dateStr) => {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  return d.toLocaleDateString('th-TH', {
    day: '2-digit', month: '2-digit', year: 'numeric'
  });
};

export default DurableDashboard;
